// Read-only panel listing the doctor's refinement instructions (the
// `refinementGoal` entered at the "Upload refinement records" step) from ALL
// refinements, shown on the refinement design submit (Ref-3) and review (Ref-5)
// surfaces for context.

import { useQuery } from '@tanstack/react-query';
import { fetchTimeline } from '../../../services/timeline';
import { formatAbsolute, formatRelative, TIMELINE_HISTORY_STALE_MS } from '@odo/shared-ui';

interface Instruction {
    id: string;
    goal: string;
    author: string;
    createdAt: string;
}

function ByLine({ author, createdAt }: { author: string; createdAt: string }) {
    return (
        <span className="text-[11px] text-gray-500 flex items-center gap-2">
            <span className="truncate max-w-[160px]">{author}</span>
            <span className="text-gray-300">·</span>
            <span title={formatAbsolute(createdAt)} className="whitespace-nowrap">
                {formatRelative(createdAt) || formatAbsolute(createdAt)}
            </span>
        </span>
    );
}

export function RefinementInstructionsPanel({ patientId }: { patientId: string }) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['refinement-instructions-all', patientId],
        queryFn: async (): Promise<Instruction[]> => {
            const res = await fetchTimeline(patientId, {
                type: 'upload-refinement-records',
                limit: 50
            });
            return res.data
                .map((event) => ({
                    id: event.id,
                    goal:
                        (
                            event.payload as { refinementGoal?: string } | undefined
                        )?.refinementGoal?.trim() ?? '',
                    author: event.createdByUser?.name ?? event.createdBy ?? 'Unknown',
                    createdAt: event.createdAt
                }))
                .filter((x) => x.goal.length > 0);
        },
        staleTime: TIMELINE_HISTORY_STALE_MS,
        enabled: Boolean(patientId)
    });

    return (
        <div className="h-full overflow-y-auto scrollbar-hide px-3 py-3 space-y-4 font-inter">
            {isLoading ? (
                <div className="text-xs text-gray-400 italic">Loading refinement instructions…</div>
            ) : isError ? (
                <div className="text-xs text-red-500">Failed to load refinement instructions.</div>
            ) : !data || data.length === 0 ? (
                <div className="text-xs text-gray-400 italic">
                    No refinement instructions recorded.
                </div>
            ) : (
                data.map((n) => (
                    <div
                        key={n.id}
                        className="border-b border-gray-100 last:border-b-0 pb-3 last:pb-0"
                    >
                        <ByLine author={n.author} createdAt={n.createdAt} />
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap break-words mt-1">
                            {n.goal}
                        </p>
                    </div>
                ))
            )}
        </div>
    );
}
